import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api, HttpError } from "../api/rest";
import type { CommandAccepted, CommandResult } from "../api/types";
import { useStatus } from "../hooks/useStatus";
import { takeProblem } from "../lib/accounts";
import { parseMode, rowAction } from "../lib/strategies";
import { PENDING_TIMEOUT_MS } from "../store/orders";
import { commandTargetNow } from "../store/useAccounts";
import { useOrders } from "../store/useOrders";

/**
 * Кнопки «взять» и «пропустить» по одной строке журнала — ступень `alert`.
 *
 * # Что уходит на сервер
 *
 * ТОЛЬКО идентификатор строки, роль и сумма. Цены нет: план пересчитывает
 * трейдер из живого стакана в момент команды — между записью строки и нажатием
 * проходят секунды, а в конце окна стакан за это время успевает уехать. Сумма
 * едет ради СВЕРКИ: трейдер отвергнет команду, если действующая успела
 * измениться в соседней вкладке.
 *
 * # Почему нет подтверждения в два шага
 *
 * Оно есть у ПЕРЕКЛЮЧЕНИЯ РЕЖИМА, где ошибка действует до следующей правки.
 * Здесь ошибка стоит одного ордера на известную сумму, а окно живёт секунды:
 * второй клик отнял бы у оператора ровно то время, ради которого ступень `alert`
 * и заведена. Тот же выбор, что у карточки замка.
 *
 * # Чем кончается «…»
 *
 * Ответ 202 означает «команда принята в шину», и не более: судьбу её называет
 * ОТДЕЛЬНЫЙ конверт command_result, который web_api рассылает всем вкладкам, а
 * store/useOrders раскладывает по trace_id. Поэтому ожидание держится до
 * вердикта по СВОЕМУ trace_id — ровно до момента, когда оператору есть что
 * увидеть.
 *
 * Двух простых решений здесь нет ни одного. Мгновенный сброс делает кнопку
 * кликабельной до ответа — это второй ордер на те же деньги. Вечное «…» не
 * лучше: инвалидация ленты строку не размонтирует, и оператор до перезагрузки
 * страницы не может ни повторить, ни нажать «мимо», ни узнать, что трейдер
 * отказал. Отсюда рубеж PENDING_TIMEOUT_MS — тот же, по которому командой
 * ордера признаётся «без ответа»: после него кнопки возвращаются с пометкой
 * «ответа нет». Вторых денег это не стоит, потому что строку занимает ТРЕЙДЕР —
 * заявка (trader.strategyTake: TakeForOrder до публикации ордера) ставится до
 * того, как ордер уйдёт, и повторное «взять» по занятой строке он отвергает.
 */
export function StrategyRowActions({
  strategy,
  signalID,
  windowEnd,
}: {
  strategy: string;
  signalID: string;
  windowEnd: string;
}) {
  const queryClient = useQueryClient();
  const status = useStatus();
  const [sent, setSent] = useState<Sent | null>(null);
  const [note, setNote] = useState<Note | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Часы тикают своим состоянием: без них строка, чьё окно кончилось, осталась
  // бы с живой кнопкой до следующего опроса ленты. Тот же тик доводит до экрана
  // и рубеж ожидания вердикта.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const verdict = useOrders((s) => (sent?.traceId ? s.verdicts[sent.traceId] : undefined));

  // Вердикт снимается СНИМКОМ, а не читается при каждом рендере: записи в store
  // живут PENDING_GIVEUP_MS и убираются, и строка, показывающая их напрямую,
  // через минуту сама себе отменила бы ответ.
  useEffect(() => {
    if (!sent || !verdict) return;
    setSent(null);
    const view = verdictView(verdict.res, sent.done);
    if (view.done) setNote(view.note);
    else setError(view.error);
  }, [sent, verdict]);

  const st = status.data?.strategies?.find((s) => s.id === strategy);
  const action = rowAction(st?.mode, windowEnd, now);

  // Собственная команда оператора важнее часов строки, и потому стои́т ВЫШЕ
  // проверок режима и окна: окно кончается через секунды после нажатия, и
  // верни мы здесь «окно кончилось», ответ трейдера оператор не увидел бы
  // вовсе — приходит он только сюда.
  if (note) {
    return (
      <span className="text-[11px] text-[var(--color-muted)]" title={note.title}>
        {note.text}
      </span>
    );
  }
  if (stillWaiting(sent, now)) {
    return <span className="text-[11px] text-[var(--color-accent)]">{sent.label}…</span>;
  }

  if (action === "none") return null;
  if (action === "late") {
    return <span className="text-[11px] text-[var(--color-muted)]">окно кончилось</span>;
  }

  // Роль берётся из ДЕЙСТВУЮЩИХ настроек, а не спрашивается кнопкой: разрешение
  // выдано оператором заранее, и предлагать здесь роль, которую трейдер молча
  // сузит до none, значило бы звонить впустую.
  const role = parseMode(st?.mode).role;
  // Сумма берётся из НОТЫ трейдера, а не считается здесь: она уезжает обратно с
  // командой, и трейдер сверяет её на равенство со своей. Посчитай мы минимум
  // сами — сверять было бы нечего, потому что обе стороны считали бы одно и то
  // же по-разному, и первое же расхождение выглядело бы как чужая правка.
  const budgetUSD = st?.order_live;
  const kinds: ("maker" | "taker")[] =
    role === "both" ? ["taker", "maker"] : role === "maker" ? ["maker"] : role === "taker" ? ["taker"] : [];
  if (kinds.length === 0) {
    return <span className="text-[11px] text-[var(--color-muted)]">роль не разрешена</span>;
  }

  async function run(
    what: () => Promise<{ command: CommandAccepted }>,
    label: string,
    done: string,
  ) {
    setError(null);
    setNote(null);
    const clicked = Date.now();
    setSent({ label, done, traceId: null, at: clicked });
    try {
      const res = await what();
      // Условие на `at` оберегает от ОБГОНА: пока 202 шёл, ожидание могло
      // кончиться по рубежу и оператор — нажать ещё раз. Запоздавший ответ не
      // имеет права подменить своим trace_id команду, которая летит сейчас.
      setSent((cur) =>
        cur && cur.at === clicked ? { ...cur, traceId: res.command.trace_id, at: Date.now() } : cur,
      );
      void queryClient.invalidateQueries({ queryKey: ["decided-signals"] });
      void queryClient.invalidateQueries({ queryKey: ["family-signals"] });
      void queryClient.invalidateQueries({ queryKey: ["consensus-signals"] });
    } catch (err) {
      setSent(null);
      setError(err instanceof HttpError ? err.message : "не удалось отправить");
    }
  }

  return (
    <span className="flex items-center gap-1">
      {kinds.map((kind) => (
        <button
          key={kind}
          type="button"
          onClick={() => {
            // Взятие — только аккаунтом (когортой сигнал не берут: он
            // забирается один раз). Цель читается в момент нажатия.
            const command = commandTargetNow();
            if (!command.account) {
              setError(takeProblem(command));
              return;
            }
            const account = command.account;
            void run(
              () => api.takeStrategySignal(strategy, signalID, kind, account, budgetUSD),
              kind === "maker" ? "заявка" : "покупка",
              "ордер заявлен",
            );
          }}
          title={
            kind === "maker"
              ? "встать в стакан по цене строки: комиссии нет, исполнение не гарантировано"
              : "перейти спред и забрать то, что есть в пределах суммы"
          }
          className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[11px] text-[var(--color-fg)]"
        >
          {(kind === "maker" ? "заявка" : "взять") + (budgetUSD ? ` $${budgetUSD}` : "")}
        </button>
      ))}
      <button
        type="button"
        onClick={() =>
          void run(() => api.skipStrategySignal(strategy, signalID), "пропуск", "пропущено")
        }
        title="закрыть строку без ордера: «посмотрел и решил не идти» — тоже факт"
        className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[11px] text-[var(--color-muted)]"
      >
        мимо
      </button>
      {error && <span className="text-[11px] text-[var(--color-sell)]">{error}</span>}
      {/* Досюда команда доживает только пережившей рубеж: пока ожидание живо,
          ячейка возвращается выше одним «покупка…» и кнопок не рисует вовсе. */}
      {sent && (
        <span
          className="text-[11px] text-[var(--color-muted)]"
          title={
            `трейдер не ответил за ${PENDING_TIMEOUT_MS / 1000} с. Команда не потеряна: ` +
            "она durable в шине. Повтор безопасен — занятую строку трейдер отвергнет"
          }
        >
          ответа нет
        </span>
      )}
    </span>
  );
}

/** Команда в полёте: ждём вердикта по её trace_id. */
interface Sent {
  /** Подпись ожидания: «покупка», «заявка», «пропуск». */
  label: string;
  /** Чем ответить на ПРИНЯТУЮ команду. */
  done: string;
  /** trace_id из ответа 202. Пусто, пока POST не вернулся. */
  traceId: string | null;
  /**
   * ЧАСЫ БРАУЗЕРА: с какого момента идёт рубеж ожидания. Сначала это момент
   * клика, после 202 — момент приёма, и переставляется он намеренно: рубеж
   * меряет молчание ТРЕЙДЕРА, а сетевой круг до web_api к этому молчанию
   * отношения не имеет. Та же договорённость, что у Pending.startedAt.
   */
  at: number;
}

/** Итог, после которого кнопок в строке больше нет. */
interface Note {
  text: string;
  title: string;
}

/**
 * Что вердикт трейдера значит для ячейки.
 *
 * Развилка одна и она про деньги: возвращать ли кнопки. Отказ — возвращать:
 * он означает, что ордера НЕТ (строку заняли раньше, сумма разошлась с
 * действующей, окно ушло), и оператору есть что делать дальше — повторить или
 * закрыть строку. «Судьба неизвестна» — не возвращать: ордер мог уйти, и
 * предложить второй значило бы предложить вторые деньги. То же правило, что в
 * store/orders.ingestResult.
 */
type VerdictView = { done: true; note: Note } | { done: false; error: string };

function verdictView(res: CommandResult, ok: string): VerdictView {
  if (res.accepted) {
    return {
      done: true,
      // «Принято» — это про КОМАНДУ: трейдер понял её и заявил ордер. Вердикт
      // биржи приедет по своему client_order_id и живёт в панели ордеров;
      // назови мы здесь ордер выставленным, строка обещала бы то, чего ещё не
      // случилось (то же правило, что у эха быстрых действий).
      note: { text: ok, title: "трейдер принял команду; вердикт биржи — в панели ордеров" },
    };
  }
  if (res.unresolved) {
    return {
      done: true,
      note: {
        text: "судьба неизвестна",
        title: res.error || "трейдер не получил ответа биржи и выясняет судьбу ордера сверкой",
      },
    };
  }
  // Текст берётся у трейдера: «по этому окну ордер уже стоит», «сумма ставки
  // изменилась» и «дневной оборот исчерпан» требуют от оператора разного, и
  // общее «не удалось» скрыло бы ровно то, ради чего он смотрит на строку.
  return { done: false, error: res.error || "трейдер отказал без объяснения" };
}

/** Жив ли ещё смысл показывать «…». */
function stillWaiting(sent: Sent | null, now: number): sent is Sent {
  return sent !== null && now - sent.at < PENDING_TIMEOUT_MS;
}
